import { useMemoizedFn } from "ahooks";
import type { CoinmarketCapToken, TokenInfo, TokenState } from "../store/types";

import { CRYPTO_THEME_LABELS } from "@componentsv1/radar/consts/themes";
import { DashboardPanelType } from "./types";
import { useSelector } from "./useValtioSelector";
import { rootStore } from "@store/rootStore";

type TokenFieldKey = keyof (TokenInfo & CoinmarketCapToken);

interface UseTokenFieldProps {
  poolAddress?: string;
  tags?: string[];
  fieldKey?: TokenFieldKey;
  fallbackValue?: string;
  panelType?: DashboardPanelType;
}

interface TokenFieldResult {
  value: string | undefined;
  isLoading: boolean;
}

export const useTokenField = ({
  poolAddress = "",
  fieldKey,
  fallbackValue,
  panelType,
}: UseTokenFieldProps): TokenFieldResult => {
  // 使用 useMemo 确保当参数变化时 selector 函数会重新创建
  const selector = useMemoizedFn(
    (state: TokenState): { value: string | number | undefined } => {
      let storeValue: string | number | undefined;

      if (panelType === CRYPTO_THEME_LABELS.blueChip) {
        // 对于蓝筹币，从 coinmarketcapTokenList 中获取
        storeValue = state.coinmarketcapTokenList.find(
          (t) => t?.id === poolAddress,
        )?.[fieldKey as keyof CoinmarketCapToken];
      } else {
        // 对于其他类型，从 tokenList 中获取
        storeValue = state.tokenList.find((t) => t?.pool === poolAddress)?.[
          fieldKey as keyof TokenInfo
        ];
      }

      return {
        value: storeValue ?? fallbackValue,
      };
    },
  );

  const { value } = useSelector<
    TokenState,
    { value: string | number | undefined }
  >(rootStore.token, selector);

  return {
    value: value as string | undefined,
    isLoading: false, // 可以根据需要添加加载状态
  };
};
